import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthenticationWrapper";
import AssetsService from "../../../../rest/assetsService";
import { toast } from "react-toastify";
import InfoField from "../../../../components/lib/infoField";
import CustomButton from "../../../../components/lib/customButton";
import ConfirmDeleteModal from "../../../../components/reusables/confirmDeleteModal";
import { formatCurrency } from "../../../../utils/CurrencyUtils";
import NutritionalFactsTableView from "../../../../components/reusables/nutritionalFactsTableView";
import NoPictureWarning from "../../../../resources/nopic.png";
import MenuService from "../../../../rest/menuService";
import ModalInfo from "../../../../components/lib/modalInfo";
import { GrCircleQuestion } from "react-icons/gr";
import RecipeEditStepper from "./recipeEditStepper";
import ConfirmCloseView from "../../../customers/components/confirmCloseView";
import Modal from "../../../../components/lib/modal";
import { useRecipeLiveUpdate } from "../../hooks/recipes/useRecipeLiveUpdate";
import { Transition } from "@headlessui/react";
import SmartImage from "../../../../components/reusables/smartImage";

const RecipeDetailsModalContent = ({ recipe, onEdit, onDelete }) => {
    const liveRecipe = useRecipeLiveUpdate(recipe);
    const { hasPermission } = useAuth();
    const isParent = sessionStorage.getItem('role') === 'PARENT';

    const canEdit = hasPermission("menu", "update") && !isParent;
    const canDelete = hasPermission("menu", "delete") && !isParent;

    const [imageData, setImageData] = useState(null);
    const [items, setItems] = useState([]);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (hasData) setConfirmClose(true);
        else setShowEditModal(false);
    };

    const forceClose = () => {
        setConfirmClose(false);
        setShowEditModal(false);
    };

    useEffect(() => {
        const getItems = () => {
            if (!liveRecipe?.ingredientsList) {
                setItems([]);
                return;
            }
            const ingredientItems = liveRecipe.ingredientsList.split(',').map(item => {
                const parts = item.split('x');
                if (parts.length >= 2) {
                    const productName = parts[0].trim();
                    const quantity = parts[1].trim();
                    if (!isNaN(quantity)) {
                        return { name: productName, quantity: quantity };
                    }
                }
                return null;
            }).filter(Boolean);
            setItems(ingredientItems);
        }

        getItems();
    }, [liveRecipe]);

    const imageUrl = `/foodz/api/v1/uploads/carregarImagemDoProduto?path=${encodeURIComponent(liveRecipe?.imageUrl)}`;

    const handleDelete = async () => {
        try {
            await MenuService.deleteRecipe(liveRecipe.recipe_id);
            toast.success("Receita removida com sucesso!");
            setShowModalDelete(false);
            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao remover receita.");
        }
    };

    const handleEditSubmit = async (updatedData) => {
        try {
            await MenuService.updateRecipe(liveRecipe.recipe_id, updatedData);
            toast.success('Receita atualizada com sucesso!');
            setShowEditModal(false);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar receita.');
        }
    };

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
        >
            <div className="flex flex-col h-[75vh] md:h-[650px] max-h-[80vh]">
                <div className="flex-1 overflow-y-auto px-4 py-2 sm:px-2">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-5/12 space-y-4">
                            {/* Imagem */}
                            <div className="w-full aspect-[5/5] sm:aspect-video lg:aspect-[5/5] bg-gray-200 rounded-2xl overflow-hidden border border-gray-200 shadow-md relative group">
                                {liveRecipe?.imageUrl ? (
                                    <SmartImage
                                        src={imageUrl}
                                        alt={liveRecipe.name}
                                        isThumb={false}
                                        className="object-cover group-hover:scale-105"
                                    />
                                ) : (
                                    <img src={NoPictureWarning} className="absolute inset-0 w-full h-full opacity-40 object-contain p-12" alt="N/A" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100" />

                                {!isParent && (
                                    <div className={`absolute top-3 left-3 z-20 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1 ${liveRecipe.active ? "bg-green-500" : "bg-red-500"}`}>
                                        {liveRecipe.active ? "ATIVO" : "INATIVO"}
                                    </div>
                                )}

                                <div className="absolute bottom-0 left-0 w-full p-4 z-10">
                                    <h2 className="text-white font-bold text-xl drop-shadow-lg truncate">
                                        {liveRecipe.name}
                                    </h2>
                                </div>
                            </div>

                            <div className={`grid ${isParent ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                                {!isParent ?
                                    <>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                            <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço</p>
                                            <p className="text-lg font-bold text-slate-800">{formatCurrency(liveRecipe.price)}</p>
                                        </div>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                            <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço Online</p>
                                            <p className="text-lg font-bold text-slate-800">{formatCurrency(liveRecipe.online_price)}</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                            <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Estoque</p>
                                            <p className="text-lg font-bold text-slate-800">{liveRecipe.quantity}</p>
                                        </div>

                                    </>
                                    : <>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                            <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço</p>
                                            <p className="text-lg font-bold text-slate-800">{formatCurrency(liveRecipe.online_price)}</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                            <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Estoque</p>
                                            <p className="text-lg font-bold text-slate-800">{liveRecipe.quantity}</p>
                                        </div>
                                    </>}
                            </div>

                            {!isParent && (
                                <InfoField
                                    label="Disponível para preparo"
                                    value={liveRecipe.quantity}
                                    labelExtra={<ModalInfo icon={<GrCircleQuestion />} content="Valor calculado com base na quantidade disponível de ingredientes para a produção da receita." />}
                                />
                            )}

                            {!isParent && (
                                <div className="py-4">
                                    <p className="flex text-base text-custom_gray_font font-semibold">Lista de Ingredientes</p>
                                    <div className="w-full max-h-64 p-1.5 text-sm font-medium text-custom_gray_font border border-solid border-gray-300 rounded overflow-y-auto">
                                        {Array.isArray(items) && items.length > 0 ?
                                            items.map((item, index) => (
                                                <div className='flex items-center justify-between border-b-2 border-slate-200 my-1 p-1 px-2' key={index}>
                                                    <p className='font-medium'>{item.name} x{item.quantity}</p>
                                                </div>
                                            )) : <p className="text-center">Nenhum ingrediente encontrado.</p>
                                        }
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="w-full lg:w-7/12 flex flex-col gap-4">
                            {!isParent && <InfoField label="SKU" value={liveRecipe.sku || "Não registrado"} />}

                            <InfoField label="Descrição" value={liveRecipe.description || "Não registrado"} />

                            {!isParent && <InfoField label="Observações" value={liveRecipe.observations || "Não registrado"} />}

                            {!isParent && (
                                <>
                                    <InfoField label="Ativo no cardápio?" value={liveRecipe.active ? " Sim" : " Não"} />
                                    <InfoField label="Precisa de Preparo?" value={liveRecipe.needsPreparation ? " Sim" : " Não"} />
                                </>
                            )}

                            <div className="py-2">
                                <NutritionalFactsTableView nutritionalFactsData={liveRecipe.nutritionalFacts} />
                            </div>
                        </div>
                    </div>
                </div>

                {!isParent && (
                    <div className="mt-auto border-t border-gray-200 bg-gray-50 p-3 sm:p-4 rounded-b-lg">
                        <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3">
                            <CustomButton
                                buttonType="edit"
                                text="Editar"
                                onClick={() => setShowEditModal(true)}
                                disabled={!canEdit}
                            />
                            <CustomButton
                                buttonType="delete"
                                text="Excluir"
                                onClick={() => setShowModalDelete(true)}
                                disabled={!canDelete}
                            />
                        </div>
                    </div>
                )}

                {showEditModal && !isParent && (
                    <Modal isOpen={showEditModal} setIsOpen={requestClose} title="Editar Receita" hideFooter maxWidth="max-w-xl lg:max-w-2xl md:-mr-16">
                        <div className="relative h-full flex flex-col">
                            <RecipeEditStepper
                                recipe={liveRecipe}
                                initialImagePreview={imageData}
                                onFinish={async ({ recipeValues, image }) => {
                                    try {
                                        let imageUrl = liveRecipe.imageUrl;
                                        if (image) {
                                            const formData = new FormData();
                                            formData.append("file", image);
                                            const upload = await AssetsService.uploadProductImage(formData);
                                            imageUrl = upload.data;
                                        }
                                        const ingredientsString = recipeValues.ingredients.map(item => `${item.name} x${item.amount}`).join(', ');
                                        const finalPayload = {
                                            recipe_id: liveRecipe.recipe_id,
                                            name: recipeValues.name,
                                            price: recipeValues.price,
                                            online_price: recipeValues.online_price,
                                            sku: recipeValues.sku,
                                            category: recipeValues.category,
                                            description: recipeValues.description,
                                            observations: recipeValues.observations,
                                            active: recipeValues.active,
                                            needsPreparation: recipeValues.needsPreparation,
                                            nutritionalFacts: JSON.stringify(recipeValues.nutrition),
                                            imageUrl: imageUrl,
                                            ingredientsList: ingredientsString,
                                            clientCode: sessionStorage.getItem("clientCode")
                                        };
                                        await handleEditSubmit(finalPayload);
                                    } catch (error) {
                                        console.error("Erro ao processar edição:", error);
                                        toast.error("Erro ao atualizar receita.");
                                    }
                                }}
                                onCancel={() => setShowEditModal(false)}
                                onDirty={() => setHasData(true)}
                            />
                            {confirmClose && (
                                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                                    <ConfirmCloseView onCancel={() => setConfirmClose(false)} onConfirm={forceClose} />
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                <ConfirmDeleteModal
                    isOpen={showModalDelete}
                    title="Excluir receita"
                    message={`Tem certeza que deseja excluir ${liveRecipe.name}?`}
                    warning="Essa ação não poderá ser desfeita."
                    onConfirm={handleDelete}
                    onCancel={() => setShowModalDelete(false)}
                />
            </div>
        </Transition>
    );
};

export default RecipeDetailsModalContent;